import React from 'react'
import l10n from '../i18n/I18N'
import Lottie from 'react-lottie'
import parse from 'html-react-parser'
import * as DOMPurify from 'dompurify'
import { Link, useLocation } from 'react-router-dom'
import PropTypes from 'prop-types'
import VisibilitySensor from 'react-visibility-sensor'
import { useStateValue } from '../../services/state/StateProvider'
import ReactGA from 'react-ga'
import ToutFormContent from './ToutFormContent'
import Cookies from 'js-cookie'
import Countdown from '../Countdown/Countdown'
import { convertUrlToWebp, getImageMimeType, isInGame, tokenizer } from '../../services/utils'
import { config } from '../Init/Init'

function ToutContentPositioned(props) {
  function getAnimationPosition(position) {
    if (position === 'left') {
      return 'xMinYMid slice'
    } else if (position === 'right') {
      return 'xMaxYMid slice'
    } else {
      return 'xMidYMid slice'
    }
  }

  const colorStyle = () => {
    if (props.data.colors) {
      if (props.data.colors.split('),').length > 1) {
        return {
          background: `linear-gradient(${props.data.colors})`,
          WebkitBackgroundClip: 'text',
          color: 'transparent'
        }
      } else {
        return {
          color: `${props.data.colors}`
        }
      }
    }
  }

  const gaMetrics = data => {
    // record tout click event in GA
    if (Cookies.get('acceptCookies')) {
      ReactGA.event({
        category: 'Banner CTA',
        action: 'A User clicked on the Banner CTA and opened: ' + data.url,
        label: 'Banner Call To Action'
      })
    }
  }

  const state = useStateValue()
  let location
  // React router context might not be available. Fallback to window.location.
  try {
    location = useLocation()
  } catch {
    location = window.location
  }
  let casino
  let animOptions = {}
  // State might not be ready yet.
  if (state) {
    casino = state[0].casino
  }

  const animJson = () => {
    let storedAnim = casino?.animations[props.data.anim]
    if (storedAnim) {
      for (let i = 0; i < storedAnim.assets.length; i++) {
        if (storedAnim.assets[i].u) {
          storedAnim.assets[i].u = `${process.env.PUBLIC_URL}/content/images/${storedAnim.nm}/`
        }
      }
    }
    return storedAnim
  }

  if (props.data.anim) {
    animOptions = {
      loop: true,
      autoplay: true,
      animationData: animJson(),
      rendererSettings: {
        preserveAspectRatio: getAnimationPosition(props.data.animationAlign)
      }
    }
  }

  if (props.type === 'image') {
    return (
      <picture>
        {props.data.hasWebp && (
          <source
            srcSet={convertUrlToWebp(process.env.PUBLIC_URL + props.data.url)}
            type={getImageMimeType(convertUrlToWebp(process.env.PUBLIC_URL + props.data.url))}
          />
        )}
        <img
          src={process.env.PUBLIC_URL + props.data.url}
          alt={props.data.text}
          className={`dis-b maw-1/1 mv- p-- pos-r ${props.data.classes ? props.data.classes : ''}`}
          type={getImageMimeType(props.data.url)}
        />
      </picture>
    )
  } else if (props.type === 'animation') {
    return (
      <div className={`maw-300px mh-a pos-r ${props.data.classes ? props.data.classes : ''}`}>
        <VisibilitySensor>{({ isVisible }) => <Lottie options={animOptions} isPaused={!isVisible} />}</VisibilitySensor>
      </div>
    )
  } else if (props.type === 'text') {
    return (
      <div className="pos-r ta-c">
        <div className={`ph+ mh-a w-1/1 ${props.data.classes ? props.data.classes : ''}`} style={colorStyle()}>
          {parse(
            DOMPurify.sanitize(props.data.text, {
              ALLOWED_TAGS: [
                'img',
                's',
                'b',
                'i',
                'u',
                'hr',
                'icon-gc',
                'icon-sc',
                'icon-sc-plus',
                'icon-dia',
                'icon-dia-plus',
                'icon-h5c'
              ],
              CUSTOM_ELEMENT_HANDLING: {
                tagNameCheck: /^(icon-sc|icon-sc-plus|icon-gc|icon-dia|icon-dia-plus|icon-h5c)/,
                attributeNameCheck: /size/
              }
            })
          )}
        </div>
      </div>
    )
  } else if (props.type === 'video-link') {
    return (
      <Link
        to="#"
        className={`${
          config.enableSweepstakes ? 'bdrs-rounded' : 'bdrs-small'
        } bgc-default bgc-default-hv c-white c-white-hv dis-ib fw-600 ls-0-05 mv- ph pos-r pv- td-n td-n-hv ${
          props.data.classes ? props.data.classes : ''
        }`}
        onClick={() => {
          gaMetrics(props.data)
          if (props.onAction) {
            props.onAction({
              type: 'ctaClicked',
              action: 'PlayVideo',
              url: props.data.url,
              ctaNumber: props.data.ctaNumber
            })
          }
        }}
      >
        {props.data.text}
      </Link>
    )
    // TODO: abstract 'close' into a tout action handler when new actions are requested
  } else if (props.type === 'link' || props.type === 'close') {
    // if a link is '/content' then we should avoid all our localization and routing logic;
    return props.data.url.indexOf('/content') === 0 ? (
      <a
        href={props.data.url}
        className={`${
          config.enableSweepstakes ? 'bdrs-rounded' : 'bdrs-small'
        } bgc-default bgc-default-hv c-white c-white-hv dis-ib fw-600 ls-0-05 mv- ph pos-r pv- td-n td-n-hv ${
          props.data.classes ? props.data.classes : ''
        }`}
        onClick={() => {
          gaMetrics(props.data)
          if (props.onAction) {
            props.onAction({
              type: 'ctaClicked',
              url: props.data.url,
              ctaNumber: props.data.ctaNumber
            })
          }
        }}
      >
        {props.data.text}
      </a>
    ) : props.data.url.indexOf('http') !== 0 ? (
      // handle internal link
      <Link
        to={l10n.localizeUrl(props.data.url)}
        state={
          props.data.url.indexOf('/') === 0
            ? {
                inGame: isInGame(location)
              }
            : undefined
        }
        className={`${
          config.enableSweepstakes ? 'bdrs-rounded' : 'bdrs-small'
        } bgc-default bgc-default-hv c-white c-white-hv dis-ib fw-600 ls-0-05 mv- ph pos-r pv- td-n td-n-hv ${
          props.data.classes ? props.data.classes : ''
        }`}
        onClick={() => {
          gaMetrics(props.data)
          if (props.onAction) {
            props.onAction({
              type: 'ctaClicked',
              url: props.data.url,
              ctaNumber: props.data.ctaNumber
            })
          }
        }}
      >
        {props.data.text}
      </Link>
    ) : (
      // open link in new tab
      <Link
        to={props.data.url}
        target="_blank"
        className={`${
          config.enableSweepstakes ? 'bdrs-rounded' : 'bdrs-small'
        } bgc-default bgc-default-hv c-white c-white-hv dis-ib fw-600 ls-0-05 mv- ph pos-r pv- td-n td-n-hv ${
          props.data.classes ? props.data.classes : ''
        }`}
        onClick={e => {
          let trackingOnly = false

          if (props.data.classes) {
            const classesArr = props.data.classes.split(' ')
            if (classesArr.indexOf('noclose') >= 0) {
              trackingOnly = true
            }
          }

          gaMetrics(props.data)
          if (props.onAction) {
            props.onAction({
              type: 'ctaClicked',
              url: props.data.url,
              ctaNumber: props.data.ctaNumber,
              trackingOnly: trackingOnly
            })
          }
        }}
      >
        {props.data.text}
      </Link>
    )
  } else if (props.type === 'button') {
    return (
      <button
        className={`${
          config.enableSweepstakes ? 'bdrs-rounded' : 'bdrs-small'
        } bds-n bgc-default bgc-default-hv c-white c-white-hv dis-ib fw-600 ls-0-05 mv- ph pos-r pv- td-n td-n-hv ${
          props.data.classes ? props.data.classes : ''
        }`}
        id={props.id ? props.id : null}
        onClick={() => {
          if (props.onAction) {
            props.onAction({
              type: 'ctaClicked',
              url: props.data.url,
              action: props.data.action,
              ctaNumber: props.data.ctaNumber
            })
          }
        }}
      >
        {props.data.text}
      </button>
    )
  } else if (props.type === 'spacer') {
    return (
      <div
        className={`pos-r ${props.data.classes ? props.data.classes : ''}`}
        style={{
          minHeight: `${props.data.space}`
        }}
      ></div>
    )
  } else if (props.type === 'placeholder') {
    return <div className={`placeholder-object-inside ${props.data.classes ? props.data.classes : ''}`}></div>
  } else if (props.type === 'form') {
    return (
      <ToutFormContent
        formData={props.asset.formData}
        url={props.asset.cta.url}
        classes={props.data.classes ? props.data.classes : ''}
        onAction={props.onAction}
      />
    )
  } else if (props.type === 'timer') {
    return (
      <div className="pos-r ta-c">
        <div className={`ph+ mh-a w-1/1 ${props.data.classes ? props.data.classes : ''}`} style={colorStyle()}>
          <Countdown deadline={props.data.deadline} timerText={props.data.text} expiredText={props.data.expiredText} />
        </div>
      </div>
    )
  } else {
    return (
      <div className="pos-r">
        {tokenizer(l10n.t['lobby.slideerror'], {
          '{type}': props.type
        })}
      </div>
    )
  }
}

ToutContentPositioned.propTypes = {
  data: PropTypes.object.isRequired
}

export default ToutContentPositioned
